import { useQuery } from '@tanstack/react-query';

import { API } from 'api';
import { apiBasePath, apiName } from 'assets/constants';

import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

type OverlayAnswersResponse = {
  nps: unknown[];
  audienceFeedback: unknown[];
};

const getOverlayAnswers = async (
  feedbackType: string,
): Promise<OverlayAnswersResponse> => {
  return API.get(
    apiName,
    `${apiBasePath}/mainaccounts/overlay`,
    { feedbackType },
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.ACTION_PLAN,
        domain: DOMAINS.FEEDBACK,
        importance: IMPORTANCE.LOW,
      },
      context: {
        feedbackType,
      },
    },
  );
};

// `includeNps: false` is for overlays whose questions never write to nps; any
// overlay NPS answer would otherwise hide them for good.
const useHasOverlayAnswers = (
  feedbackType: string,
  { includeNps = true }: { includeNps?: boolean } = {},
) => {
  const query = useQuery<OverlayAnswersResponse>({
    queryKey: ['mainaccounts', 'overlay', feedbackType],
    queryFn: () => getOverlayAnswers(feedbackType),
    enabled: !!feedbackType,
  });

  const hasAnyAnswers =
    (includeNps && (query.data?.nps?.length ?? 0) > 0) ||
    (query.data?.audienceFeedback?.length ?? 0) > 0;

  return {
    hasAnswers: hasAnyAnswers,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export default useHasOverlayAnswers;
